import { useState } from "react"
import { useLazyQuery } from "@apollo/client"
import { useNavigate } from "react-router-dom"
import { handlePath } from "~/common/paths"
import { SearchAutoComplete } from "~/ui/search-autocomplete"
import { gql } from "~/__generated__"

const USER_SEARCH_QUERY = gql(/* GraphQL */ `
  query UserSearch($query: String!) {
    userSearch(query: $query) {
      contactSearchId
      user {
        id
        handle
        firstName
        lastName
        fullName
        wallets {
          id
          publicKey
          publicKeyFormatted
        }
        avatarThumbUrl
        emails {
          email
        }
        phones {
          phoneFormatted
        }
      }
    }
  }
`)

export interface UserSearchProps {
  placeholder?: string
  className?: string
}

export const UserSearch = ({
  placeholder = "Search email, phone, or wallet address...",
  className,
}: UserSearchProps) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedUserId, setSelectedUserId] = useState("")

  const [loadUserFromSearch, { loading, data }] = useLazyQuery(USER_SEARCH_QUERY)

  // Transform search data for SearchAutoComplete
  const searchResult = data?.userSearch?.user
  const contactSearchId = data?.userSearch?.contactSearchId
  const searchItems =
    searchResult && contactSearchId
      ? [
          {
            value: searchResult.id,
            label: searchResult.fullName || searchResult.handle || "Unknown User",
            user: {
              ...searchResult,
              contactSearchId,
            },
          },
        ]
      : []

  const handleSearch = () => {
    if (searchQuery.trim()) {
      loadUserFromSearch({ variables: { query: searchQuery } })
    }
  }

  const handleUserSelection = (userId: string) => {
    const selectedUser = searchItems.find((item) => item.value === userId)
    if (selectedUser?.user?.handle && selectedUser.user.contactSearchId) {
      const contactSearchId = selectedUser.user.contactSearchId
      navigate(`${handlePath({ handle: selectedUser.user.handle })}?csrid=${contactSearchId}`)
      // Don't reset immediately - let the user manually clear the search if they want
    }
  }

  const handleReset = () => {
    setSearchQuery("")
    setSelectedUserId("")
  }

  return (
    <div className={className}>
      <SearchAutoComplete
        selectedValue={selectedUserId}
        onSelectedValueChange={handleUserSelection}
        searchValue={searchQuery}
        onSearchValueChange={setSearchQuery}
        items={searchItems}
        isLoading={loading}
        emptyMessage="User was not found"
        placeholder={placeholder}
        onSubmit={handleSearch}
        onReset={handleReset}
      />
    </div>
  )
}
